import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {Col, Form, Row} from "react-bootstrap";
import ProgressBar from "react-bootstrap/ProgressBar";

import 'react-bootstrap-typeahead/css/Typeahead.css';
import {Typeahead} from 'react-bootstrap-typeahead';
import {Filter} from "../../../components/filters/filterGroup";
import {DDLDataMode, DDLMode, GENERIC_DDL_TYPE} from "../../../components/dropDownLists/genericDDL";
import {clientToStateMap} from "../../../utils/textValueLists";

const componentName = "UserModalForm";
const NAME_VALIDATION_PATTERN = /[^0-9a-z]/i;
const EMAIL_VALIDATION_PATTERN = /^[^\s@]+@[^.\s@]+(?:\.[^.\s@]+)+$/;

const FormGroup = ({ hidden, required, pattern, name, type, value, onChange, className, label, showValidationErrors }) => {
  const [error, setError] = useState("")
  const [touched, setTouched] = useState(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const getValidationError = useCallback((nextValue) => {
    if (required && !nextValue) {
      return `${name.toUpperCase()} is required`;
    }
    if (pattern) {
      if (type === "text" && nextValue?.match(pattern)) {
        return `Invalid entry in  field ${name.toUpperCase()}`;
      }
      if (type === "email" && nextValue && !nextValue?.match(pattern)) {
        return `Invalid entry in  field ${name.toUpperCase()}`;
      }
    }
    return '';
  }, [name, pattern, required, type]);

  const handleChange = useCallback((e) => {
    const hasError = getValidationError(e.target.value);
    setTouched(true);
    setError(hasError);
    e.error = hasError
    onChangeRef.current(e)
  }, [getValidationError])

  useEffect(() => {
    const hasError = getValidationError(value);
    const isRequiredError = required && !value;
    const shouldShowError = !hidden && (showValidationErrors || touched || (hasError && !isRequiredError));
    setError(shouldShowError ? hasError : "");
    const e = { "target": { "name": name, "value": value } }
    e.error = shouldShowError ? hasError : "";
    onChangeRef.current(e)
  }, [getValidationError, hidden, name, required, showValidationErrors, touched, value]);

  return (
    <Form.Group hidden={hidden} className="mb-3">
      <Form.Label>
        <span id={`${componentName}-${name}Label`}>{label}</span><span className="text-danger">*</span>
      </Form.Label>
      <Form.Control
        type={type}
        className={className}
        hidden={hidden}
        name={name}
        id={`${componentName}-${name}Value`}
        required={required}
        value={value}
        error={error}
        onChange={(e) => handleChange(e)}
      />
      <span className="text-danger">{error}</span>
    </Form.Group>
  )
}

export const UserModalForm = ({
  type,
  handleChange,
  user,
  roles,
  organizations,
  accessTypes,
  clients,
  error,
  showValidationErrors = false
}) => {
  const isEmpty = require("is-empty");
  const errors = useSelector((state) => state.errors);
  const { loading } = useSelector((state) => state.userManagement);
  const formInputsStateRef = useRef({})

  let getSelectedOrganization = (user, organizations) => {
    if (user.organization != null) {
      let selected = null;
      for (const organization of organizations) {
        if (organization.id === user.organization.id) {
          selected = organization;
          break;
        }
      }

      if (selected != null) {
        const out = [];
        out.push(selected);
        return out;
      }
    }

    return [];
  };

  const [filterValues, setFilterValues] = useState({
    paramValue: user?.client?.length ? user.client:[],
    displayValue: user?.client?.length ? user.client.map(e=>clientToStateMap[e]):[],
  });

  const handleChangeWithinModal = (e, error) => {
    handleChange(e, error)
  }


  const handleChangeWithFilter = (e, gapId, abb) => {
    setFilterValues(e);
    handleChange(e, error)
  }

  return (
    <>
      {loading && isEmpty(errors) && <ProgressBar animated now={95} />}
      <Row xs={1} md={2}>
        <Col>
          <FormGroup label={"First Name"} hidden={type === "BULK UPDATE USER"}
            type="text"
            className={"form-control"}
            name="firstName"
            pattern={NAME_VALIDATION_PATTERN}
            value={user.firstName || ""}
            required={true}
            onChange={(e) => handleChangeWithinModal(e)}
            error={formInputsStateRef}
            showValidationErrors={showValidationErrors}
          />
        </Col>
        <Col>
          <FormGroup label={"Last Name"} hidden={type === "BULK UPDATE USER"}
            type="text"
            className={"form-control"}
            name="lastName"
            pattern={NAME_VALIDATION_PATTERN}
            value={user.lastName || ""}
            required={true}
            onChange={(e) => handleChangeWithinModal(e)}
            error={formInputsStateRef}
            showValidationErrors={showValidationErrors}

          />
        </Col>
        <Col>
          <FormGroup label={"Email (User ID)"} hidden={type === "BULK UPDATE USER"}
            type="email"
            className="form-control"
            disabled={type === "UPDATE USER"}
            name="email"
            pattern={EMAIL_VALIDATION_PATTERN}
            value={user.email || ""}
            required={true}
            onChange={(e) => handleChangeWithinModal(e, error)}
            error={formInputsStateRef}
            showValidationErrors={showValidationErrors}
          />
        </Col>
        <Col>
          <Filter
            handleChange={(e) => handleChangeWithFilter(e)}
            selected={filterValues}
            filterDefinition={{
              type: GENERIC_DDL_TYPE,
              getHeader: () => "Clients",
              key: "Clients",
              componentProps: {
                mode: DDLMode.MultipleSelect,
                dataMode: DDLDataMode.OneAPICallFilterInUI,
                showSearchTextbox: false,
                highlightWhenHasValue: false,
                showClearButton: true,
                showRequired: true,
                getData: (srchTxt) =>
                  Promise.resolve(clients),
              },
            }}
          />
        </Col>
        <Col>
          <Form.Label>
            <span id={`${componentName}-organizationLabel`}>Organization</span><span className="text-danger">*</span>
          </Form.Label>
          <Typeahead
            id={`${componentName}-organizationValue`}
            inputProps={{
              id: `${componentName}-organizationValue`,
              className: "form-select"
            }}
            labelKey="name"
            multiple={false}
            defaultSelected={getSelectedOrganization(user, organizations)}
            clearButton={true}
            positionFixed={true}
            flip={true}
            onChange={(e) => {
              e.target = {};
              e.target.name = "organization";
              e.target.value = e[0];

              handleChangeWithinModal(e);
            }}
            options={organizations}
            placeholder="Choose an organization"
          />
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>
              <span id={`${componentName}-roleLabel`}>Role</span><span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              className="form-select"
              defaultValue={user.userRole ? user.userRole : ""}
              name="userRole"
              id={`${componentName}-roleValue`}
              // defaultValue={user.roles.length ? user.roles[0].roleName : user.roles[0].role}
              onChange={(e) => handleChangeWithinModal(e)}
            >
              {user.userRole ? (
                <option value={user.userRole.name}>{user.userRole.name}</option>
              ) : (
                <option value="">--Select</option>
              )}
              {roles.map((role) =>
                user.userRole?.name !== role.name ? (
                  <option
                    key={role.id}
                    value={JSON.stringify({ name: role.name, id: role.id })}
                  >
                    {role.name}
                  </option>
                ) : (
                  ""
                )
              )}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>
              <span id={`${componentName}-accessTypeLabel`}>Access Type</span><span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              className="form-select"
              defaultValue={user.accessType ? user.accessType : ""}
              name="accessType"
              id={`${componentName}-accessTypeValue`}
              onChange={(e) => handleChangeWithinModal(e)}
            >
              {user.accessType ? (
                <option value={user.accessType}>{user.accessType}</option>
              ) : (
                <option value="">--Select</option>
              )}
              {accessTypes.map((access) =>
                user.accessType !== access ? (
                  <option key={access} value={access}>
                    {access}
                  </option>
                ) : (
                  ""
                )
              )}
            </Form.Control>
          </Form.Group>
        </Col>
        {user.accessType !== "NETWORK" && (
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>
                <span id={`${componentName}-identifierLabel`}>Identifier</span><span className="text-danger">*</span>
              </Form.Label>
              <input
                type="text"
                className="form-control"
                name="identifier"
                id={`${componentName}-identifierLabel`}
                value={user.identifier || ""}
                onChange={(e) => handleChangeWithinModal(e)}
              />
            </Form.Group>
          </Col>
        )}
        {type === "UPDATE USER" && (
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>
                <span id={`${componentName}-statusLabel`}>Status</span>
              </Form.Label>
              <Form.Control
                as="select"
                className="form-select"
                name="accountActive"
                id={`${componentName}-statusValue`}
                onChange={(e) => handleChangeWithinModal(e)}
              >
                <option value={user.accountActive}>
                  {user.accountActive ? "Active" : "Inactive"}
                </option>
                {user.accountActive ? (
                  <option value={false}>Inactive</option>
                ) : (
                  <option value={true}>Active</option>
                )}
              </Form.Control>
            </Form.Group>
          </Col>
        )}
      </Row>
    </>
  );
};
